import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { GridColumns, GridRowIdGetter, GridRowParams } from '@mui/x-data-grid';
import { Trans, useTranslation } from 'react-i18next';
import styled from '@mui/styles/styled';
import Button from '@mui/material/Button';
import { Link, useLocation } from 'react-router-dom';
import { CellContentTooltip } from '../CellContentTooltip/CellContentTooltip';
import { UrlQueryDataGrid } from '../UrlQueryDataGrid/UrlQueryDataGrid';
import { ELicenseMode, ILicense } from '../../models/ILicense';
import { createStatusChipComponent } from '../StatusChip/StatusChip';
import { dateFormat, DateFormat } from '../../helpers/dateFormat';
import { LazyUser, LazyUserContextProvider } from '../LazyData/LazyUser';
import { OrganizationAllLicensesContext } from '../../contexts/OrganizationAllLicensesContext';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { useFreeTextFilter } from '../../hooks/useFreeTextFilter';
import { getDateBeforeFilterOperator, getDateAfterFilterOperator } from '../../filterOperators/dateFilterOperators';
import { getEnumIsFilterOperator, getEnumIsNotFilterOperator } from '../../filterOperators/enumFilterOperator';
import { IOrganizationAllLicenseItem } from '../../models/IOrganizationAllLicenseItem';
import { RowButtonsContainer } from '../RowButtonsContainer/RowButtonsContainer';
import { useQueryUrl } from '../../hooks/useQueryUrl';
import { apiLicense } from '../../resources/api/apiLicense';
import { apiUserDownloads } from '../../resources/api/apiUserDownloads';
import { AssignLicenseModal } from '../AssignLicenseModal/AssignLicenseModal';
import { useCloseDialog } from '../../hooks/useCloseDialog';
import { useQueryParam } from '../../hooks/useQueryParam';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import { OfflineAccessStepsDialog } from '../Licenses/OfflineAccessStepsDialog';
import { ReleaseLicenseStepsDialog } from '../Licenses/ReleaseLicenseStepsDialog';
import { LicenseDetailsModal } from '../LicenseDetailsModal/LicenseDetailsModal';
import { LicenseContext } from '../../contexts/LicenseContext';
import { LicensesContext } from '../../contexts/LicensesContext';
import { licenseResources } from '../../resources/builders/licenseResources';
import { useResourceChecker } from '../../resources/resource';
import { TLazyUserInfo } from '../../contexts/LazyUsersContext';

const NotInUse = styled('span')(({ theme }) => ({
  color: theme.palette.text.disabled,
}));

const LicenseModeChip = createStatusChipComponent({
  [ELicenseMode.Online]: ['common.licenseMode.online', 'lime'],
  [ELicenseMode.Airgap]: ['common.licenseMode.offline', 'blue'],
  [ELicenseMode.Offline]: ['common.licenseMode.offline', 'blue'],
  [ELicenseMode.Unused]: ['common.licenseMode.notInUse', 'default'],
  [ELicenseMode.Unassigned]: ['common.licenseMode.unassigned', 'default'],
  [ELicenseMode.Error]: ['common.licenseMode.error', 'red'],
});

const MACHINE_EM_DASH = '—';

const RETRY_COUNT = 5;
const RETRY_DELAY = 1000;

type TAllLicenseAction = 'assign' | 'unassign' | 'download' | 'release' | 'details' | null;

type RowItem = IOrganizationAllLicenseItem & { index: number };

const closeDialogOpts = { replace: false };

const { useAssignLicense, useUnassignLicense, useReleaseOnline, useGetLicenseDetails } = apiLicense;
const { useGetFile } = apiUserDownloads;

/** Maps All Licenses API item to ILicense for LicenseContext and modals that expect ILicense. */
function mapAllLicenseItemToLicense(item: IOrganizationAllLicenseItem): ILicense {
  return {
    ...item,
    userUuid: item.assignedUserId ?? undefined,
    mode: item.mode as ELicenseMode,
  } as ILicense;
}

const RenderUser: FC<{ user: TLazyUserInfo }> = ({ user }) => (
  <span>
    {user.first_name} {user.last_name}
  </span>
);

/**
 * Row action buttons for All Licenses table. Mirrors LicensesTable: Download (airgap), Unassign (unused),
 * Release (online/offline/airgap), Assign (unassigned). Buttons that need license id are only shown when row.id is present.
 */
const AllLicenseRowButtons: FC<{ row: RowItem }> = ({ row }) => {
  const { t } = useTranslation();
  const { organization } = useContext(OrganizationContext);

  const downloadUrl = useQueryUrl({ license: row.id?.toString() ?? null, action: 'download' });
  const unassignUrl = useQueryUrl({ license: row.id?.toString() ?? null, action: 'unassign' });
  const releaseUrl = useQueryUrl({ license: row.id?.toString() ?? null, action: 'release' });
  const assignUrl = useQueryUrl({ package: row.packageId.toString(), action: 'assign' });

  const { Guard: GuardAssign } = useAssignLicense(organization ?? void 0, 'any');
  const { Guard: GuardUnassign } = useUnassignLicense(organization ?? void 0, row.assignedUserId ?? undefined, row.id);
  const { Guard: GuardRelease } = useReleaseOnline(organization ?? void 0, row.assignedUserId ?? undefined, row.id);
  const { Guard: GuardDownload } = useGetFile();

  const hasLicenseId = row.id != null;

  return (
    <RowButtonsContainer>
      <GuardDownload>
        {hasLicenseId && row.mode === ELicenseMode.Airgap ? (
          <Button component={Link} to={downloadUrl} variant="outlined" color="primary" tabIndex={-1}>
            {t('licenses.table.downloadButton')}
          </Button>
        ) : null}
      </GuardDownload>

      {hasLicenseId && row.mode === ELicenseMode.Unused ? (
        <GuardUnassign>
          <Button component={Link} to={unassignUrl} variant="outlined" color="primary" tabIndex={-1}>
            {t('licenses.table.unassignButton')}
          </Button>
        </GuardUnassign>
      ) : null}

      <GuardRelease>
        {hasLicenseId &&
        [ELicenseMode.Online, ELicenseMode.Offline, ELicenseMode.Airgap].includes(row.mode as ELicenseMode) ? (
          <Button component={Link} to={releaseUrl} variant="outlined" color="primary" tabIndex={-1}>
            {t('licenses.table.releaseButton')}
          </Button>
        ) : null}
      </GuardRelease>

      {row.mode === ELicenseMode.Unassigned ? (
        <GuardAssign>
          <Button component={Link} to={assignUrl} variant="outlined" color="primary" tabIndex={-1}>
            {t('licenses.table.assignButton')}
          </Button>
        </GuardAssign>
      ) : null}
    </RowButtonsContainer>
  );
};

/**
 * Handles assign modal when URL has action=assign and package=<packageId>.
 * Resolves the batch item by packageId and passes a minimal license shape to AssignLicenseModal.
 */
const AllLicensesAssignModal: FC = () => {
  const packageIdParam = useQueryParam('package');
  const action = useQueryParam('action') as TAllLicenseAction;
  const { licenses, onRefresh } = useContext(OrganizationAllLicensesContext);
  const { organization } = useContext(OrganizationContext);
  const closeDialog = useCloseDialog(void 0, ['package', 'action'], closeDialogOpts);

  const packageId = packageIdParam ? parseInt(packageIdParam, 10) : NaN;
  const batchItem = licenses?.find((l) => l.packageId === packageId && l.mode === 'unassigned') ?? null;

  if (!organization || !batchItem || action !== 'assign') return null;

  const licenseForModal: ILicense = {
    name: batchItem.name,
    packageId: batchItem.packageId,
    customerProductId: batchItem.customerProductId,
  } as ILicense;

  return (
    <AssignLicenseModal
      isOpen={true}
      closeDialog={closeDialog}
      organization={organization}
      license={licenseForModal}
      onRefresh={onRefresh}
    />
  );
};

/**
 * Handles modals for a specific license when URL has license=<id> and action (unassign, download, release, details).
 * Provides LicenseContext with the selected row mapped to ILicense and renders the same dialogs as LicensesTable.
 */
const AllLicensesLicenseModals: FC = () => {
  const { t } = useTranslation();
  const { organization } = useContext(OrganizationContext);
  const { licenses, onRefresh } = useContext(OrganizationAllLicensesContext);
  const handleError = useApiErrorHandler();

  const licenseIdParam = useQueryParam('license');
  const action = useQueryParam('action') as TAllLicenseAction;
  const mode = useQueryParam('mode');

  const closeDialog = useCloseDialog(void 0, ['license', 'package', 'action', 'mode'], closeDialogOpts);

  const rawLicenseId = licenseIdParam ? parseInt(licenseIdParam, 10) : NaN;
  const licenseId = Number.isInteger(rawLicenseId) ? rawLicenseId : NaN;
  const licenseItem = licenses?.find((l) => l.id === licenseId) ?? null;
  const license = licenseItem ? mapAllLicenseItemToLicense(licenseItem) : null;

  const { execute: unassign } = useUnassignLicense(organization ?? void 0, license?.userUuid, license?.id);
  const { Guard: GuardDetails } = useGetLicenseDetails(organization ?? void 0, license?.userUuid, license?.id);

  const onUnassign = useCallback(async () => {
    try {
      await unassign();
      await onRefresh();
      closeDialog();
    } catch (e) {
      handleError(e);
    }
  }, [unassign, onRefresh, closeDialog, handleError]);

  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    setRetryCount(0);
  }, [licenseId]);

  useEffect(() => {
    if (mode === ELicenseMode.Airgap && license && license.mode !== ELicenseMode.Airgap && retryCount < RETRY_COUNT) {
      const timer = setTimeout(async () => {
        await onRefresh();
        setRetryCount((prev) => prev + 1);
      }, RETRY_DELAY);
      return () => clearTimeout(timer);
    }
  }, [mode, license, retryCount, onRefresh]);

  if (!organization || !license) return null;

  return (
    <LicensesContext.Provider value={{ licenses: null, onRefresh }}>
      <LicenseContext.Provider value={license}>
        <LazyUserContextProvider id={license.userUuid}>
          <ConfirmationDialog
            open={action === 'unassign' && !!license.userUuid}
            title={t('customerUser.packages.unassignDialog.title')}
            message={
              <Trans
                i18nKey="customerUser.packages.unassignDialog.message"
                values={{ license: license.name }}
                components={{
                  user: license.userUuid ? <LazyUser id={license.userUuid} Render={RenderUser} /> : null,
                }}
              />
            }
            confirm={onUnassign}
            abort={closeDialog}
          />

          <OfflineAccessStepsDialog
            open={action === 'download'}
            onClose={closeDialog}
            isLoading={mode === ELicenseMode.Airgap && license.mode !== ELicenseMode.Airgap && retryCount < RETRY_COUNT}
          />

          <ReleaseLicenseStepsDialog open={action === 'release'} onClose={closeDialog} />

          <GuardDetails>
            {action === 'details' ? <LicenseDetailsModal isOpen={true} onClose={closeDialog} /> : null}
          </GuardDetails>
        </LazyUserContextProvider>
      </LicenseContext.Provider>
    </LicensesContext.Provider>
  );
};

/**
 * Table for the All Licenses page. Uses API: GET /licensing/org/{organizationUuid}/licenses.
 * Columns: License, Mode, Activation, Assigned User, Machine, Buttons.
 * Must be rendered inside OrganizationAllLicensesContext and OrganizationContext.
 */
export const AllLicensesTable: FC = () => {
  const { licenses } = useContext(OrganizationAllLicensesContext);
  const { organization } = useContext(OrganizationContext);
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const { search } = useLocation();
  const resourceChecker = useResourceChecker();

  const rows = useMemo<RowItem[]>(() => {
    return (licenses ?? []).map((license, arrayIndex) => ({
      ...license,
      index: arrayIndex + 1,
    }));
  }, [licenses]);

  const columns = useMemo<GridColumns>(
    () => [
      {
        field: 'name',
        headerName: t('common.tableHeader.license'),
        flex: 2,
        renderCell: ({ value }: { value: string }) => <CellContentTooltip>{value}</CellContentTooltip>,
      },
      {
        field: 'mode',
        headerName: t('common.tableHeader.mode'),
        width: 120,
        filterOperators: [
          getEnumIsFilterOperator({
            [t('common.licenseMode.notInUse')]: [ELicenseMode.Unused],
            [t('common.licenseMode.online')]: [ELicenseMode.Online],
            [t('common.licenseMode.offline')]: [ELicenseMode.Offline, ELicenseMode.Airgap],
            [t('common.licenseMode.unassigned')]: [ELicenseMode.Unassigned],
          }),
          getEnumIsNotFilterOperator({
            [t('common.licenseMode.notInUse')]: [ELicenseMode.Unused],
            [t('common.licenseMode.online')]: [ELicenseMode.Online],
            [t('common.licenseMode.offline')]: [ELicenseMode.Offline, ELicenseMode.Airgap],
            [t('common.licenseMode.unassigned')]: [ELicenseMode.Unassigned],
          }),
        ],
        renderCell: ({ value }: { value: string }) => <LicenseModeChip value={value as ELicenseMode} />,
      },
      {
        field: 'activationDate',
        headerName: t('allLicenses.activation'),
        width: 180,
        renderCell: ({ row }: { row: RowItem }) =>
          row.activationDate
            ? t('allLicenses.activationActivated', {
                date: dateFormat(row.activationDate, DateFormat.DateMedium, language),
              })
            : t('allLicenses.activationNotActivated'),
        valueFormatter: ({ value }: { value: unknown }) =>
          value && typeof value === 'string'
            ? dateFormat(value, DateFormat.DateMedium, language)
            : t('allLicenses.activationNotActivated'),
        filterOperators: [getDateBeforeFilterOperator(), getDateAfterFilterOperator()],
      },
      {
        field: 'assignedUserId',
        headerName: t('allLicenses.assignedUser'),
        flex: 1,
        renderCell: ({ row }: { row: RowItem }) =>
          row.assignedUserId ? (
            <LazyUser id={row.assignedUserId} />
          ) : (
            <NotInUse>{t('allLicenses.unassigned')}</NotInUse>
          ),
      },
      {
        field: 'machineName',
        headerName: t('allLicenses.machine'),
        width: 140,
        renderCell: (params) => {
          const value = params.value as string | null;
          if (!value) {
            return <NotInUse>{MACHINE_EM_DASH}</NotInUse>;
          }
          return <CellContentTooltip>{value}</CellContentTooltip>;
        },
        filterable: false,
      },
      {
        field: 'buttons',
        headerName: t('common.tableHeader.buttons'),
        renderHeader: () => ' ',
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <LazyUserContextProvider id={params.row.assignedUserId ?? undefined}>
            <AllLicenseRowButtons row={params.row} />
          </LazyUserContextProvider>
        ),
        width: 280,
      },
    ],
    [t, language]
  );

  const getRowId: GridRowIdGetter = useCallback((row) => `${row.packageId}/${row.index}`, []);

  const getRowLink = useCallback(
    (params: GridRowParams) => {
      if (!params.row.id || !organization) return null;
      const resource = licenseResources.getLicenseDetails(
        organization,
        params.row.assignedUserId ?? undefined,
        params.row.id
      );
      if (!resourceChecker(resource)) return null;
      const newSearch = new URLSearchParams(search);
      newSearch.set('license', params.row.id.toString());
      newSearch.set('action', 'details');
      return `?${newSearch.toString()}`;
    },
    [organization, resourceChecker, search]
  );

  const filteredRows = useFreeTextFilter(rows, columns);

  if (!licenses) return null;

  return (
    <div>
      <UrlQueryDataGrid
        columns={columns}
        rows={filteredRows}
        disableExtendRowFullWidth={false}
        autoHeight={true}
        disableSelectionOnClick={true}
        getRowId={getRowId}
        getRowLink={getRowLink}
      />
      <AllLicensesLicenseModals />
      <AllLicensesAssignModal />
    </div>
  );
};
